import React, { useState, useRef } from 'react';
import { Form, Input, Button, Table, DatePicker, Select, Space, Tag, Modal, Descriptions, message, Avatar, Breadcrumb, ConfigProvider, Card } from 'antd';
import { useAntdTable, useSize } from 'ahooks';
import { UserOutlined, SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import zhCN from 'antd/lib/locale/zh_CN';
import request from '../../../../utils/request';
import MyButton from '../../../../components/MyButton';
import styles from './index.module.scss';
import { useSelector } from 'react-redux';

const { RangePicker } = DatePicker;

// 状态映射
const ORDER_STATUS_MAP = {
  CREATED: { text: '已创建', color: 'default' },
  PROCESSING: { text: '处理中', color: 'processing' },
  COMPLETED: { text: '已完成', color: 'success' },
  FAILED: { text: '失败', color: 'error' },
  CANCELED: { text: '已取消', color: 'default' },
};

const PAYMENT_METHOD_MAP = {
  OFFLINE: '线下转账',
  WECHAT_PAY: '微信支付',
  ALIPAY: '支付宝',
};

const ORDER_TYPE_MAP = {
  ENTERPRISE: '企业充值',
  PERSONAL: '个人充值',
};

const OrderManagement = () => {
  const [form] = Form.useForm();
  const [detailVisible, setDetailVisible] = useState(false);
  const [currentDetail, setCurrentDetail] = useState(null);
  
  // 用于计算表格高度
  const tableWrapperRef = useRef(null);
  const size = useSize(tableWrapperRef);
  const userInfo = useSelector((state) => state.login.userInfo);
  
  // 获取基础域名
  const getBaseUrl = () => {
    return process.env.REACT_APP_BASE_URL || window.location.origin;
  };

  // 列表接口
  const getTableData = async ({ current, pageSize }, formData) => {
    const { order_time, ...rest } = formData;
    
    const params = {
      page: current,
      page_size: pageSize,
      order_type: userInfo?.co_type_code === 'INDIV_ORG' ? 'PERSONAL' : 'ENTERPRISE',
      ...rest,
    };

    // 个人版企业传个人id,企业版传企业id
    if(userInfo?.co_type_code === 'INDIV_ORG'){
      params.user_id = userInfo.co_user_id;
    } else {
      params.co_id = userInfo.co_id;
    }

    if (order_time && order_time.length === 2) {
      params.order_time_start = moment(order_time[0]).format('YYYY-MM-DD HH:mm:ss');
      params.order_time_end = moment(order_time[1]).format('YYYY-MM-DD HH:mm:ss');
    }

    try {
      const res = await request({
        url: '/api/opm/topup/order/list/',
        method: 'get',
        baseURL: getBaseUrl(),
        notMsg: true,
        params,
      });
      if (res.data && res.data?.code === 0) {
        return {
          total: res.data?.data?.total_count,
          list: res.data?.data?.data,
        };
      } else {
        message.error(res.data?.message || '获取订单列表失败');
        return { total: 0, list: [] };
      }
    } catch (error) {
      message.error('系统错误，请稍后重试');
      return { total: 0, list: [] };
    }
  };

  const { tableProps, search } = useAntdTable(getTableData, {
    defaultPageSize: 10,
    form,
    refreshDeps: [userInfo],
  });

  const { submit, reset } = search;

  // 查看详情
  const handleViewDetail = async (record) => {
    try {
      const res = await request({
        url: '/api/opm/topup/order/detail/',
        method: 'get',
        baseURL: getBaseUrl(),
        notMsg: true,
        params: {
          order_code: record.order_code,
        },
      });
      if (res.code === 0) {
        setCurrentDetail(res.data);
        setDetailVisible(true);
      } else {
        message.error(res.msg || '获取详情失败');
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const columns = [
    {
      title: '订单编号',
      dataIndex: 'order_code',
      key: 'order_code',
      ellipsis: true,
      width: 180,
      className: 'paddingHeader',
    },
    {
      title: '订单类型',
      dataIndex: 'order_type',
      key: 'order_type',
      ellipsis: true,
      width: 100,
      render: (text) => ORDER_TYPE_MAP[text] || text || '权益充值',
    },
    {
      title: '订单名称',
      dataIndex: 'order_name',
      key: 'order_name',
      ellipsis: true,
      width: 150,
      render: (text, record) => text || record.topup_plan_name || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'order_time',
      key: 'order_time',
      ellipsis: true,
      width: 160,
    },
    {
      title: '支付方式',
      dataIndex: 'payment_method_code',
      key: 'payment_method_code',
      ellipsis: true,
      width: 120,
      render: (text) => PAYMENT_METHOD_MAP[text] || text || '-',
    },
    {
      title: '订单金额(元)',
      dataIndex: 'order_amount',
      key: 'order_amount',
      ellipsis: true,
      width: 120,
      render: (val) => val ? `¥ ${Number(val || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-',
    },
    {
      title: '实际支付(元)',
      dataIndex: 'payment_amount',
      key: 'payment_amount',
      ellipsis: true,
      width: 120,
      render: (val) => `¥ ${Number(val || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
    {
      title: '订单状态',
      dataIndex: 'order_status_code',
      key: 'order_status_code',
      ellipsis: true,
      width: 100,
      render: (status) => {
        const config = ORDER_STATUS_MAP[status] || { text: status, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '付款人',
      dataIndex: 'payer_name',
      key: 'payer_name',
      ellipsis: true,
      width: 120,
      render: (text, record) => {
        const name = text || record.orderer_name || '-';
        return (
          <Space>
            {/* <Avatar 
              size="small" 
              style={{ backgroundColor: '#1890ff' }} 
              icon={<UserOutlined />}
            >
              {name && name !== '-' ? name[0] : null}
            </Avatar> */}
            <span>{name}</span>
          </Space>
        );
      },
    },
    // {
    //   title: '操作',
    //   key: 'action',
    //   width: 80,
    //   align: 'center',
    //   fixed: 'right',
    //   render: (_, record) => (
    //     <Button 
    //       type="text" 
    //       onClick={() => handleViewDetail(record)}
    //       style={{ color: '#016DB4' }}
    //     >
    //       详情
    //     </Button>
    //   ),
    // },
  ];

  return (
    <ConfigProvider locale={zhCN}>
      <div className={styles.orderManagementWrapper}>
        <div className="right_content">
          {/* 面包屑 */}
          <Breadcrumb>
            <Breadcrumb.Item>账户管理</Breadcrumb.Item>
            <Breadcrumb.Item>订单</Breadcrumb.Item>
          </Breadcrumb>

          {/* 搜索卡片 */}
          <Card className="search-card">
            <div className={styles.searchHeader}>
              <Form form={form} layout="inline" className="form-search">
                <Form.Item name="order_time" label="订单时间">
                  <RangePicker style={{ width: 240 }} allowClear />
                </Form.Item>
                <Form.Item name="order_code" label="订单编号">
                  <Input placeholder="请输入订单编号" allowClear style={{ width: 180 }} />
                </Form.Item>
                <Form.Item name="topup_plan_name" label="订单名称">
                  <Input placeholder="请输入订单名称" allowClear style={{ width: 180 }} />
                </Form.Item>
              </Form>
              <Space>
                <MyButton type="primary" onClick={submit} icon={<SearchOutlined />}>
                  查询
                </MyButton>
                <MyButton type="default" onClick={reset}>重置</MyButton>
              </Space>
            </div>
          </Card>

          {/* 表格区域 */}
          <div className="right_table" ref={tableWrapperRef}>
            <Table
              columns={columns}
              rowKey="order_code"
              {...tableProps}
              size="small"
              scroll={{ y: size ? size.height - 95 + 'px' : 400 }} // 增加横向滚动以容纳更多列
              pagination={{
                ...tableProps.pagination,
                showTotal: (total) => `共 ${total} 条`,
                showQuickJumper: true,
                showSizeChanger: true,
              }}
            />
          </div>
        </div>

        <Modal
          title="订单详情"
          visible={detailVisible}
          onCancel={() => setDetailVisible(false)}
          footer={[
            <MyButton key="close" onClick={() => setDetailVisible(false)}>
              关闭
            </MyButton>,
          ]}
          width={800}
          bodyStyle={{ padding: '24px 40px' }}
        >
          {currentDetail && (
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="订单编号">{currentDetail.order_code}</Descriptions.Item>
              <Descriptions.Item label="订单类型">{ORDER_TYPE_MAP[currentDetail.order_type] || currentDetail.order_type}</Descriptions.Item>
              <Descriptions.Item label="订单名称">{currentDetail.order_name || currentDetail.topup_plan_name}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{currentDetail.order_time}</Descriptions.Item>
              <Descriptions.Item label="支付时间">{currentDetail.payment_time || '-'}</Descriptions.Item>
              <Descriptions.Item label="支付方式">{PAYMENT_METHOD_MAP[currentDetail.payment_method_code] || currentDetail.payment_method_code}</Descriptions.Item>
              <Descriptions.Item label="订单金额">{currentDetail.order_amount ? `¥ ${Number(currentDetail.order_amount || 0).toFixed(2)}` : '-'}</Descriptions.Item>
              <Descriptions.Item label="实际支付金额">{`¥ ${Number(currentDetail.payment_amount || 0).toFixed(2)}`}</Descriptions.Item>
              <Descriptions.Item label="订单状态">
                 {ORDER_STATUS_MAP[currentDetail.order_status_code]?.text || currentDetail.order_status_code}
              </Descriptions.Item>
              <Descriptions.Item label="付款人">{currentDetail.payer_name || currentDetail.orderer_name}</Descriptions.Item>
              <Descriptions.Item label="备注" span={2}>{currentDetail.remark || '-'}</Descriptions.Item>
            </Descriptions>
          )}
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default OrderManagement;
